import { hasLoggedRequestError, markLoggedRequestError } from '../loggableError'

/** RnB error codes, exactly as emitted by RnbErrorCodes.cs (uppercase). */
export const RNB_ERROR_CODE = {
  PRICE_TABLE_LIMIT_REACHED: 'LMT-001',
} as const

export type RnbErrorCode = (typeof RNB_ERROR_CODE)[keyof typeof RNB_ERROR_CODE]

/**
 * Code -> message id. Values MUST be written as literal strings so that
 * .github/workflows/key-checker.sh can git grep them.
 *
 * To support a new code: add it to RNB_ERROR_CODE, choose a semantic id here,
 * and add the copy to every locale in packages/messages/promotions.
 */
export const RNB_ERROR_MESSAGE_ID: Record<RnbErrorCode, string> = {
  [RNB_ERROR_CODE.PRICE_TABLE_LIMIT_REACHED]:
    'admin/promotions.limits.price-table.exceeded',
}

/** Plain object thrown by promotion write mutations when the request fails. */
export type RnbApiError = Error & {
  status: number
  statusText?: string
  url?: string
  /** Normalized (trimmed + uppercased) RnB error code, when detectable. */
  code?: string
  /** Raw response body as text, when readable. Capped, never parsed for display. */
  rawBody?: string
}

const RAW_BODY_MAX_LENGTH = 512

/** Matches "LMT-001: message", tolerating casing and padding around the code. */
const CODE_PREFIX_PATTERN = /^\s*([a-z]{2,6}-\d{2,4})\s*:/i

/** Matches a discrete code field whose whole value is the code. */
const BARE_CODE_PATTERN = /^\s*([a-z]{2,6}-\d{2,4})\s*$/i

const CODE_FIELDS = ['code', 'errorCode', 'Code', 'ErrorCode']
const MESSAGE_FIELDS = ['message', 'Message', 'error', 'Error']

function normalizeCode(code: string) {
  return code.trim().toUpperCase()
}

/** True when the value looks like a fetch Response (duck-typed, cross-realm safe). */
export function isResponseLike(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false

  const candidate = value as Response

  return (
    typeof candidate.status === 'number' && typeof candidate.text === 'function'
  )
}

function extractCodeFromJson(body: string): string | undefined {
  let parsed: unknown

  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  if (typeof parsed !== 'object' || parsed === null) return undefined

  const record = parsed as Record<string, unknown>

  for (const field of CODE_FIELDS) {
    const value = record[field]

    if (typeof value === 'string' && BARE_CODE_PATTERN.test(value)) {
      return normalizeCode(value)
    }
  }

  for (const field of MESSAGE_FIELDS) {
    const value = record[field]
    const match = typeof value === 'string' && CODE_PREFIX_PATTERN.exec(value)

    if (match) return normalizeCode(match[1])
  }

  return undefined
}

/**
 * Extracts an RnB error code from a response body.
 * Accepts, in order:
 *   1. plain text prefixed with a code:      "LMT-001: The limit ..."
 *   2. JSON object with a code field:        { "code" | "errorCode" | "Code": "LMT-001" }
 *   3. JSON object whose message is prefixed:{ "message" | "Message" | "error": "LMT-001: ..." }
 * Returns undefined for anything else. Never throws.
 * Matching is case-insensitive; the result is trimmed and uppercased.
 */
export function extractRnbErrorCode(
  body: string | undefined
): string | undefined {
  if (!body) return undefined

  const prefixMatch = CODE_PREFIX_PATTERN.exec(body)

  if (prefixMatch) return normalizeCode(prefixMatch[1])

  return extractCodeFromJson(body)
}

/**
 * A body can be consumed only once, so read through clone() and bail out when
 * another consumer got there first.
 */
async function readResponseBody(
  response: Response
): Promise<string | undefined> {
  try {
    if (response.bodyUsed) return undefined

    const source =
      typeof response.clone === 'function' ? response.clone() : response

    return await source.text()
  } catch {
    return undefined
  }
}

/**
 * Converts the value thrown by fetcherWrapper into an RnbApiError.
 * - Response-like input: reads the body via clone() (skipped when bodyUsed),
 *   extracts the code, preserves status/statusText/url.
 * - Anything else (TypeError from a network failure, AbortError, ...):
 *   returned unchanged, so existing behavior is preserved.
 * Never throws; a parse failure yields code: undefined.
 */
export async function toRnbApiError(error: unknown): Promise<unknown> {
  if (!isResponseLike(error)) return error

  try {
    const response = error as Response
    const rawBody = await readResponseBody(response)
    const code = extractRnbErrorCode(rawBody)

    const apiError = new Error(
      `RnB request failed with status ${response.status}`
    ) as RnbApiError

    apiError.name = 'RnbApiError'
    apiError.status = response.status

    if (response.statusText) apiError.statusText = response.statusText
    if (response.url) apiError.url = response.url
    if (code) apiError.code = code
    if (rawBody) apiError.rawBody = rawBody.slice(0, RAW_BODY_MAX_LENGTH)
    if (hasLoggedRequestError(error)) markLoggedRequestError(apiError)

    return apiError
  } catch {
    return error
  }
}

/** True when the value carries the enriched error shape. Excludes raw Responses. */
export function isRnbApiError(error: unknown): error is RnbApiError {
  if (typeof error !== 'object' || error === null) return false

  const candidate = error as Partial<RnbApiError>

  return (
    typeof candidate.status === 'number' &&
    typeof candidate.message === 'string'
  )
}

/** Reads the normalized code off an already-enriched error. Never throws. */
export function getRnbErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  const { code } = error as Partial<RnbApiError>

  if (typeof code !== 'string') return undefined

  const normalized = normalizeCode(code)

  return normalized.length > 0 ? normalized : undefined
}

/**
 * Resolves an error to a message id, or undefined when the caller should keep
 * its existing fallback copy. This is the only function UI code needs.
 */
export function getRnbErrorMessageId(error: unknown): string | undefined {
  const code = getRnbErrorCode(error)

  if (!code) return undefined

  return RNB_ERROR_MESSAGE_ID[code as RnbErrorCode]
}
